<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div class="card-header" :class="headerClass">
      <h5 class="mb-0"><i class="fa me-2" :class="headerIcon"></i>{{ headerTitle }}</h5>
    </div>
    <div class="card-body">
      <!-- HTTP-level submit error (couldn't even reach Odoo) -->
      <div v-if="!success" class="alert alert-danger outline mb-3">
        <i class="fa fa-exclamation-circle me-2"></i>
        <strong class="d-block mb-1">Could Not Send To Odoo</strong>
        {{ message || 'The submission failed before Odoo received it.' }}
        <div class="oa-result-help mt-2">
          Your entries haven't been lost — go back, adjust, and try again.
        </div>
      </div>

      <!-- Odoo processing status (only when HTTP submit succeeded) -->
      <template v-if="success">
        <div class="oa-result-section">
          <div class="oa-result-section__head">
            <span class="oa-result-eyebrow">Odoo Processing</span>
            <button
              type="button"
              class="oa-result-link"
              :disabled="loadingStatus"
              @click="loadStatus"
            >
              <i class="fa fa-refresh me-1" :class="{ 'fa-spin': loadingStatus }"></i>
              Refresh
            </button>
          </div>

          <div v-if="loadingStatus && !latestQueueMessage" class="d-flex align-items-center gap-2">
            <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
            <span>Checking Odoo for the processing result…</span>
          </div>

          <div v-else-if="loadError" class="alert alert-warning outline mb-0">
            <i class="fa fa-exclamation-triangle me-2"></i>
            Could not read the latest queue.message — {{ loadError }}
          </div>

          <div v-else-if="!latestQueueMessage" class="alert alert-info outline mb-0">
            <i class="fa fa-info-circle me-2"></i>
            Odoo hasn't recorded a queue entry yet for this order. Click
            <strong>Refresh</strong> in a moment.
          </div>

          <div v-else>
            <div class="oa-result-status" :class="`oa-result-status--${outcome.tone}`">
              <i class="fa" :class="outcome.iconClass" aria-hidden="true"></i>
              <div>
                <div class="oa-result-status__headline">{{ outcome.headline }}</div>
                <div class="oa-result-status__detail">{{ outcome.detail }}</div>
              </div>
            </div>

            <!-- Failure reason from queue.message.result (the user-facing text) -->
            <div
              v-if="outcome.tone === 'negative' && latestQueueMessage.result"
              class="oa-result-failure"
            >
              <div class="oa-result-failure__label">Reason Reported By Odoo</div>
              <pre class="oa-result-failure__text">{{ latestQueueMessage.result }}</pre>
            </div>

            <!-- Auto-refresh hint while still in-flight -->
            <div v-if="outcome.tone === 'waiting'" class="oa-result-pending small text-muted mt-2">
              <i class="fa fa-clock-o me-1"></i>
              Auto-refreshing every {{ pollIntervalSec }} s. Stops when Odoo finishes ({{
                elapsedSec
              }}
              s elapsed).
            </div>
          </div>
        </div>

        <!-- Invoice (if any new ones have been attached) -->
        <div v-if="hasInvoiceData" class="oa-result-section mt-3">
          <div class="oa-result-section__head">
            <span class="oa-result-eyebrow">Invoice In Odoo</span>
            <button
              type="button"
              class="oa-result-link"
              :disabled="loadingInvoice"
              @click="loadInvoiceData"
            >
              <i class="fa fa-refresh me-1" :class="{ 'fa-spin': loadingInvoice }"></i>
              Refresh
            </button>
          </div>
          <div v-for="inv in invoiceData" :key="inv.id" class="oa-result-invoice">
            <div class="oa-result-invoice__row">
              <span class="oa-result-invoice__name">{{ inv.name || `#${inv.id}` }}</span>
              <span
                v-if="inv.invoice_status"
                class="badge ms-2"
                :class="invoiceStatusBadge(inv.invoice_status)"
              >
                {{ inv.invoice_status }}
              </span>
              <span class="oa-result-invoice__meta">
                {{ inv.invoice_count != null ? `${inv.invoice_count} invoice(s)` : '' }}
              </span>
            </div>
            <dl class="oa-result-invoice__dates">
              <div v-if="inv.date_order">
                <dt>Order date</dt>
                <dd><ZonedDateTime :value="inv.date_order" :zone="userTimezone" kind="user" /></dd>
              </div>
              <div v-if="inv.create_date">
                <dt>Created</dt>
                <dd><ZonedDateTime :value="inv.create_date" :zone="userTimezone" kind="user" /></dd>
              </div>
              <div v-if="inv.write_date">
                <dt>Updated</dt>
                <dd><ZonedDateTime :value="inv.write_date" :zone="userTimezone" kind="user" /></dd>
              </div>
            </dl>
            <table
              v-if="inv.attachments && inv.attachments.length"
              class="table table-sm mb-0 oa-result-invoice__files-table"
            >
              <thead>
                <tr>
                  <th>File</th>
                  <th>Created</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="att in inv.attachments" :key="att.id">
                  <td>
                    <i class="fa fa-file-pdf-o me-2 text-danger"></i>
                    <strong>{{ att.name || att.fileName || `attachment ${att.id}` }}</strong>
                    <span v-if="att.id != null" class="text-muted small ms-2">ID {{ att.id }}</span>
                  </td>
                  <td class="small text-nowrap">
                    <ZonedDateTime :value="att.create_date" :zone="userTimezone" kind="user" />
                  </td>
                  <td class="text-end">
                    <div class="d-flex gap-1 justify-content-end flex-wrap">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary"
                        :disabled="!attachmentHasData(att)"
                        :title="
                          attachmentHasData(att)
                            ? 'Open the PDF in a new tab'
                            : 'No file data returned by Odoo'
                        "
                        @click="previewAttachment(att)"
                      >
                        <i class="fa fa-eye me-1"></i>Preview
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="!attachmentHasData(att)"
                        :title="
                          attachmentHasData(att)
                            ? 'Download the PDF'
                            : 'No file data returned by Odoo'
                        "
                        @click="downloadAttachment(att)"
                      >
                        <i class="fa fa-download me-1"></i>Download
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Technical diagnostics (collapsible) ------------------------- -->
        <div class="oa-tech-disclosure mt-3">
          <button
            type="button"
            class="oa-tech-disclosure__toggle"
            :aria-expanded="showTech"
            aria-controls="oa-tech-body"
            @click="showTech = !showTech"
          >
            <i class="fa fa-code oa-tech-disclosure__icon" aria-hidden="true"></i>
            <span class="oa-tech-disclosure__label">Technical Details</span>
            <span class="oa-tech-disclosure__hint">
              queue.message body + REST API request / response
            </span>
            <i
              class="fa oa-tech-disclosure__chev"
              :class="showTech ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
          <div v-show="showTech" id="oa-tech-body" class="oa-tech-disclosure__body">
            <!-- queue.message detail -->
            <div v-if="latestQueueMessage" class="oa-tech-block">
              <div class="oa-tech-block__head">Latest queue.message</div>
              <dl class="oa-tech-meta">
                <div>
                  <dt>ID</dt>
                  <dd>
                    <code>{{ latestQueueMessage.id }}</code>
                  </dd>
                </div>
                <div>
                  <dt>State</dt>
                  <dd>
                    <code>{{ latestQueueMessage.state || '—' }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Operation type</dt>
                  <dd>
                    <code>{{ latestQueueMessage.operation_type || '—' }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Write date</dt>
                  <dd>
                    <ZonedDateTime
                      :value="latestQueueMessage.write_date"
                      :zone="userTimezone"
                      kind="user"
                    />
                  </dd>
                </div>
                <div>
                  <dt>Error flag</dt>
                  <dd>{{ latestQueueMessage.error || '—' }}</dd>
                </div>
              </dl>
              <details class="oa-tech-details">
                <summary>Full Message Body</summary>
                <pre class="oa-tech-pre">{{
                  formatFullMessage(latestQueueMessage.full_message)
                }}</pre>
              </details>
            </div>

            <!-- kw.api.log: REST API request/response -->
            <div class="oa-tech-block">
              <div class="oa-tech-block__head">REST API Request / Response (kw.api.log)</div>

              <button
                v-if="!apiLogsFetched && !loadingApiLogs"
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="loadApiLogs"
              >
                <i class="fa fa-plug me-1"></i>Load Request / Response Logs
              </button>

              <div v-else-if="loadingApiLogs" class="d-flex align-items-center gap-2 small">
                <span class="spinner-border spinner-border-sm text-primary"></span>
                Loading API logs…
              </div>

              <div v-else-if="apiLogsError" class="alert alert-warning outline small mb-0">
                Could not load API logs — {{ apiLogsError }}
              </div>

              <div v-else-if="apiLogs.length === 0" class="small text-muted">
                No REST API logs were recorded for this order in Odoo's retention window.
              </div>

              <div v-else class="table-responsive">
                <table
                  class="table table-bordered table-striped table-hover table-sm mb-0 oa-tech-table"
                >
                  <thead>
                    <tr>
                      <th>Log ID</th>
                      <th>Time</th>
                      <th>HTTP</th>
                      <th>Outcome</th>
                      <th>Error</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in apiLogs" :key="`oa-kw-${log.id}`">
                      <td>
                        <code>{{ log.id }}</code>
                      </td>
                      <td class="small text-nowrap">
                        <ZonedDateTime
                          :value="log.writeDate || log.createDate"
                          :zone="userTimezone"
                          kind="user"
                        />
                      </td>
                      <td class="small">{{ log.httpCode || '—' }}</td>
                      <td class="small">
                        <span
                          v-if="log.isLikelyValidationFailure"
                          class="badge bg-warning text-dark"
                          >Validation error</span
                        >
                        <span
                          v-else-if="log.httpCode === '200' || log.httpCode === '201'"
                          class="badge bg-success"
                          >OK</span
                        >
                        <span v-else class="badge bg-secondary">Other</span>
                      </td>
                      <td class="small text-danger">
                        {{ log.errorMessage || log.logErrorField || '—' }}
                      </td>
                      <td>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary"
                          @click="expandedLogId = expandedLogId === log.id ? null : log.id"
                        >
                          {{ expandedLogId === log.id ? 'Hide' : 'Details' }}
                        </button>
                      </td>
                    </tr>
                    <tr v-if="expandedLog">
                      <td colspan="6" class="oa-tech-log-detail">
                        <details open>
                          <summary>Request Body</summary>
                          <pre class="oa-tech-pre">{{
                            formatFullMessage(expandedLog.requestBody)
                          }}</pre>
                        </details>
                        <details>
                          <summary>Response Body</summary>
                          <pre class="oa-tech-pre">{{
                            formatFullMessage(expandedLog.responseBody)
                          }}</pre>
                        </details>
                        <details v-if="expandedLog.validationErrors?.length">
                          <summary>Validation Errors</summary>
                          <ul class="mb-0">
                            <li
                              v-for="(ve, i) in expandedLog.validationErrors"
                              :key="i"
                              class="text-danger"
                            >
                              {{ ve }}
                            </li>
                          </ul>
                        </details>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Actions ------------------------------------------------------- -->
      <div class="d-flex gap-2 flex-wrap mt-3">
        <a
          v-if="findDataLink !== '#'"
          :href="findDataLink"
          target="_blank"
          rel="noopener"
          class="btn btn-outline-primary btn-sm"
        >
          <i class="fa fa-external-link me-1"></i>Order Details
        </a>
        <a
          v-if="findDataLink !== '#'"
          :href="findDataLink"
          target="_blank"
          rel="noopener"
          class="btn btn-outline-primary btn-sm"
        >
          <i class="fa fa-external-link me-1"></i>Open in Customer Support
        </a>
        <button class="btn btn-outline-secondary btn-sm" @click="$emit('restart')">
          <i class="fa fa-refresh me-1"></i>Update another order
        </button>
        <button v-if="showBackButton" class="btn btn-primary btn-sm" @click="$emit('back')">
          <i class="fa fa-arrow-left me-1"></i>Back to payment details
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';

const toast = useToast();
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);

const props = defineProps({
  success: { type: Boolean, default: false },
  message: { type: String, default: '' },
  uuid: { type: String, default: '' },
  /**
   * The queue.message id (if any) that was visible BEFORE this submission.
   * Lets us treat a returned message with the same or lower id as "stale" —
   * Odoo may take a moment to create the new entry after our POST.
   */
  previousQueueMessageId: { type: [Number, String, null], default: null },
});

defineEmits(['restart', 'back']);

const POLL_INTERVAL_MS = 5_000;
const POLL_MAX_DURATION_MS = 120_000;

const loadingStatus = ref(false);
const loadingInvoice = ref(false);
const loadError = ref('');
const latestQueueMessage = ref(null);
const invoiceData = ref([]);

const apiLogs = ref([]);
const apiLogsFetched = ref(false);
const loadingApiLogs = ref(false);
const apiLogsError = ref('');
const expandedLogId = ref(null);
const showTech = ref(false);

let pollHandle = null;
let pollStartedAt = 0;
const elapsedSec = ref(0);
const pollIntervalSec = POLL_INTERVAL_MS / 1000;

const findDataLink = computed(() =>
  props.uuid
    ? `/ryd-customer-support/find-data-by-order-uuid?uuid=${encodeURIComponent(props.uuid)}`
    : '#',
);

const outcome = computed(() => {
  const m = latestQueueMessage.value;
  if (!m) {
    return {
      tone: 'neutral',
      iconClass: 'fa-question-circle',
      headline: 'No result yet',
      detail: 'Odoo has not produced a queue entry for this submission.',
    };
  }
  const s = String(m.state || '').toLowerCase();
  if (s === 'done') {
    return {
      tone: 'positive',
      iconClass: 'fa-check-circle',
      headline: 'Odoo finished processing successfully.',
      detail: 'The payment was accepted and the invoice flow can continue.',
    };
  }
  if (s === 'failed') {
    return {
      tone: 'negative',
      iconClass: 'fa-times-circle',
      headline: 'Odoo reported a failure.',
      detail: 'See the reason below and the technical details for the full diagnostics.',
    };
  }
  if (s === 'new' || s === 'processing') {
    return {
      tone: 'waiting',
      iconClass: 'fa-spinner fa-spin',
      headline: s === 'new' ? 'Queued in Odoo.' : 'Processing in Odoo.',
      detail: 'Refreshing automatically until Odoo finishes.',
    };
  }
  return {
    tone: 'neutral',
    iconClass: 'fa-exclamation-circle',
    headline: `Unexpected state: ${m.state}`,
    detail: 'See the technical details for raw data.',
  };
});

const hasInvoiceData = computed(() => invoiceData.value && invoiceData.value.length > 0);

const expandedLog = computed(() => apiLogs.value.find((l) => l.id === expandedLogId.value) || null);

const showBackButton = computed(() => !props.success || outcome.value.tone === 'negative');

const headerClass = computed(() => {
  if (!props.success) return 'bg-warning text-dark';
  switch (outcome.value.tone) {
    case 'positive':
      return 'bg-success text-white';
    case 'negative':
      return 'bg-danger text-white';
    case 'waiting':
      return 'bg-primary text-white';
    default:
      return 'bg-secondary text-white';
  }
});

const headerIcon = computed(() => {
  if (!props.success) return 'fa-exclamation-circle';
  switch (outcome.value.tone) {
    case 'positive':
      return 'fa-check-circle';
    case 'negative':
      return 'fa-times-circle';
    case 'waiting':
      return 'fa-spinner fa-spin';
    default:
      return 'fa-info-circle';
  }
});

const headerTitle = computed(() => {
  if (!props.success) return 'Send Failed';
  switch (outcome.value.tone) {
    case 'positive':
      return 'Payment Sent To Odoo';
    case 'negative':
      return 'Odoo Could Not Process The Payment';
    case 'waiting':
      return 'Odoo Is Still Processing…';
    default:
      return 'Payment Sent — Checking Status';
  }
});

function invoiceStatusBadge(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'invoiced') return 'bg-success';
  if (s === 'to invoice') return 'bg-warning text-dark';
  if (s === 'no') return 'bg-secondary';
  return 'bg-secondary';
}

function formatFullMessage(raw) {
  if (raw === false || raw == null || raw === '') return '—';
  if (typeof raw !== 'string') {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function isFreshlyCreated(message) {
  if (!message?.id) return false;
  const prev = props.previousQueueMessageId;
  if (prev == null) return true;
  // queue.message ids are numeric and monotonic in Odoo — newer rows have
  // higher ids.
  return Number(message.id) > Number(prev);
}

async function loadStatus() {
  if (!props.uuid) return;
  loadingStatus.value = true;
  loadError.value = '';
  try {
    const res = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.odooSaleOrder.queueMessages(props.uuid),
    );
    const payload = res.data?.data ?? null;
    const messages = payload?.queueMessages || [];
    const latest = messages[0] ?? null;
    // Don't show a stale queue.message as if it were the result of this submit.
    // If the returned id is not newer than what we had before, treat as "still
    // pending" and let the poll loop pick it up.
    latestQueueMessage.value = isFreshlyCreated(latest) ? latest : null;
  } catch (err) {
    loadError.value =
      err?.response?.data?.message ?? err?.message ?? 'Failed to load processing status.';
  } finally {
    loadingStatus.value = false;
  }
}

async function loadInvoiceData() {
  if (!props.uuid) return;
  loadingInvoice.value = true;
  try {
    const res = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.invoiceCancellationModification.search(props.uuid),
      { params: { includeFiles: true } },
    );
    const payload = res.data;
    if (payload?.success && Array.isArray(payload.data)) {
      invoiceData.value = payload.data
        .filter((o) => (o.invoice_count ?? 0) > 0 || (o.attachments?.length ?? 0) > 0)
        .map((o) => ({
          id: o.id,
          name: o.name,
          invoice_status: o.invoice_status,
          invoice_count: o.invoice_count,
          date_order: o.date_order,
          create_date: o.create_date,
          write_date: o.write_date,
          attachments: o.attachments || [],
        }));
    } else {
      invoiceData.value = [];
    }
  } catch {
    // Non-blocking — invoice may not be ready yet.
    invoiceData.value = [];
  } finally {
    loadingInvoice.value = false;
  }
}

function attachmentHasData(attachment) {
  return !!(attachment && (attachment.datas || attachment.data));
}

function base64ToBlob(base64, contentType = 'application/pdf') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

function attachmentMime(attachment) {
  if (attachment?.mimetype) return attachment.mimetype;
  const name = String(attachment?.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}

function previewAttachment(attachment) {
  const data = attachment?.datas ?? attachment?.data;
  if (!data) {
    toast.error('No file data was returned by Odoo for this attachment.');
    return;
  }
  try {
    const fileURL = URL.createObjectURL(base64ToBlob(data, attachmentMime(attachment)));
    const w = window.open(fileURL, '_blank');
    if (w) w.opener = null;
    // Release the URL after a tick so the new tab has time to load.
    setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
  } catch (e) {
    console.error('Preview failed:', e);
    toast.error('Could not open the attachment — the file data looks corrupted.');
  }
}

function downloadAttachment(attachment) {
  const data = attachment?.datas ?? attachment?.data;
  if (!data) {
    toast.error('No file data was returned by Odoo for this attachment.');
    return;
  }
  try {
    const fileURL = URL.createObjectURL(base64ToBlob(data, attachmentMime(attachment)));
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = attachment.name || `attachment-${attachment.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(fileURL), 0);
  } catch (e) {
    console.error('Download failed:', e);
    toast.error('Could not download the attachment — the file data looks corrupted.');
  }
}

async function loadApiLogs() {
  if (!props.uuid) return;
  loadingApiLogs.value = true;
  apiLogsError.value = '';
  try {
    const res = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.odooSaleOrder.queueMessageApiLogs(props.uuid),
    );
    const payload = res.data?.data ?? res.data;
    apiLogs.value = Array.isArray(payload?.entries) ? payload.entries : [];
    apiLogsFetched.value = true;
  } catch (err) {
    apiLogsError.value = err?.response?.data?.message ?? err?.message ?? 'Failed to load API logs.';
    apiLogsFetched.value = true;
  } finally {
    loadingApiLogs.value = false;
  }
}

function clearPolling() {
  if (pollHandle !== null) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

function maybeStartPolling() {
  if (!props.success || !props.uuid) return;
  const tone = outcome.value.tone;
  if (tone !== 'waiting' && tone !== 'neutral') {
    clearPolling();
    return;
  }
  if (pollHandle !== null) return;
  pollStartedAt = pollStartedAt || Date.now();
  pollHandle = setInterval(async () => {
    elapsedSec.value = Math.floor((Date.now() - pollStartedAt) / 1000);
    if (Date.now() - pollStartedAt > POLL_MAX_DURATION_MS) {
      clearPolling();
      return;
    }
    await loadStatus();
    await loadInvoiceData();
    const newTone = outcome.value.tone;
    if (newTone !== 'waiting' && newTone !== 'neutral') {
      clearPolling();
    }
  }, POLL_INTERVAL_MS);
}

function openFindData() {
  if (findDataLink.value === '#') return;
  window.open(findDataLink.value, '_blank', 'noopener');
}

watch(
  () => outcome.value.tone,
  (tone) => {
    if (tone === 'waiting' || tone === 'neutral') {
      maybeStartPolling();
    } else {
      clearPolling();
    }
  },
);

onMounted(async () => {
  if (!props.success) return;
  pollStartedAt = Date.now();
  // Wait a beat — Odoo needs a moment between our POST and the queue.message landing.
  await new Promise((r) => setTimeout(r, 1_000));
  await Promise.all([loadStatus(), loadInvoiceData()]);
  maybeStartPolling();
});

onBeforeUnmount(() => {
  clearPolling();
});
</script>

<style scoped>
.oa-result-help {
  color: var(--bs-body-color, #212529);
  text-transform: none;
}

/* Section -------------------------------------------------------------- */
.oa-result-section {
  padding: 0.85rem 0;
}

.oa-result-section + .oa-result-section {
  border-top: 1px dashed rgba(15, 23, 42, 0.1);
  margin-top: 0.5rem;
}

.oa-result-section__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.6rem;
}

.oa-result-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.74rem;
  font-weight: 800;
  color: var(--theme-default);
}

.oa-result-link {
  background: transparent;
  border: 0;
  color: var(--theme-default);
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 0.35rem;
}

.oa-result-link:hover,
.oa-result-link:focus-visible {
  background: rgba(0, 161, 254, 0.1);
  outline: none;
}

.oa-result-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Status card --------------------------------------------------------- */
.oa-result-status {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #fff;
}

.oa-result-status > i {
  font-size: 1.4rem;
  margin-top: 0.05rem;
}

.oa-result-status__headline {
  font-weight: 700;
  color: var(--bs-body-color, #212529);
  margin-bottom: 0.15rem;
}

.oa-result-status__detail {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.88rem;
}

.oa-result-status--positive {
  border-color: rgba(35, 183, 103, 0.3);
  background: rgba(35, 183, 103, 0.05);
}

.oa-result-status--positive > i {
  color: var(--bs-success, #198754);
}

.oa-result-status--negative {
  border-color: rgba(226, 36, 70, 0.3);
  background: rgba(226, 36, 70, 0.05);
}

.oa-result-status--negative > i {
  color: var(--bs-danger, #dc3545);
}

.oa-result-status--waiting {
  border-color: rgba(0, 161, 254, 0.25);
  background: rgba(0, 161, 254, 0.05);
}

.oa-result-status--waiting > i {
  color: var(--theme-default);
}

.oa-result-status--neutral > i {
  color: var(--bs-secondary-color, #6c757d);
}

/* Failure detail ----------------------------------------------------- */
.oa-result-failure {
  margin-top: 0.85rem;
  border: 1px solid rgba(226, 36, 70, 0.22);
  border-radius: 0.45rem;
  background: rgba(226, 36, 70, 0.05);
  padding: 0.65rem 0.85rem;
}

.oa-result-failure__label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.7rem;
  font-weight: 800;
  color: var(--bs-danger, #b02a37);
  margin-bottom: 0.35rem;
}

.oa-result-failure__text {
  margin: 0;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--bs-body-color, #212529);
  font-family: 'Courier New', Courier, monospace;
}

.oa-result-pending {
  display: inline-block;
}

/* Invoice block ------------------------------------------------------ */
.oa-result-invoice {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.45rem;
  padding: 0.7rem 0.85rem;
  background: #fff;
  margin-bottom: 0.5rem;
}

.oa-result-invoice__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.oa-result-invoice__name {
  font-weight: 700;
  color: var(--bs-body-color, #212529);
}

.oa-result-invoice__meta {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.82rem;
  margin-left: auto;
}

.oa-result-invoice__dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  margin: 0.5rem 0 0.65rem 0;
  font-size: 0.82rem;
}

.oa-result-invoice__dates > div {
  display: flex;
  gap: 0.4rem;
}

.oa-result-invoice__dates dt {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--bs-secondary-color, #6c757d);
  align-self: center;
}

.oa-result-invoice__dates dd {
  margin: 0;
  color: var(--bs-body-color, #212529);
  font-weight: 500;
}

.oa-result-invoice__files-table {
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

.oa-result-invoice__files-table thead th {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.68rem;
  color: var(--bs-secondary-color, #6c757d);
  font-weight: 700;
  border-bottom-width: 1px;
}

.oa-result-invoice__files-table td {
  vertical-align: middle;
}

/* Technical disclosure ------------------------------------------------- */
.oa-tech-disclosure {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.55rem;
  background: #fbfcff;
  overflow: hidden;
}

.oa-tech-disclosure__toggle {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  color: var(--bs-body-color, #212529);
  transition: background 0.15s ease;
}

.oa-tech-disclosure__toggle:hover,
.oa-tech-disclosure__toggle:focus-visible {
  background: rgba(0, 161, 254, 0.05);
  outline: none;
}

.oa-tech-disclosure__icon {
  width: 1.7rem;
  height: 1.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
  background: rgba(46, 52, 130, 0.07);
  color: var(--theme-default);
  font-size: 0.8rem;
}

.oa-tech-disclosure__label {
  font-weight: 600;
  color: var(--bs-body-color, #212529);
}

.oa-tech-disclosure__hint {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.8rem;
}

.oa-tech-disclosure__chev {
  margin-left: auto;
  color: var(--bs-secondary-color, #6c757d);
}

.oa-tech-disclosure__body {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  padding: 0.85rem 1rem;
  background: #fff;
}

.oa-tech-block + .oa-tech-block {
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px dashed rgba(15, 23, 42, 0.1);
}

.oa-tech-block__head {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--bs-secondary-color, #6c757d);
  margin-bottom: 0.5rem;
}

.oa-tech-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 1.5rem;
  margin: 0 0 0.5rem 0;
  font-size: 0.85rem;
}

.oa-tech-meta > div {
  display: flex;
  gap: 0.5rem;
}

.oa-tech-meta dt {
  color: var(--bs-secondary-color, #6c757d);
  font-weight: 600;
  min-width: 8rem;
}

.oa-tech-meta dd {
  margin: 0;
  color: var(--bs-body-color, #212529);
}

.oa-tech-details summary {
  cursor: pointer;
  color: var(--theme-default);
  font-size: 0.85rem;
  font-weight: 600;
}

.oa-tech-pre {
  background: rgba(46, 52, 130, 0.04);
  border-radius: 0.35rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  max-height: 22rem;
  overflow: auto;
  margin-top: 0.4rem;
  color: var(--bs-body-color, #212529);
}

.oa-tech-table {
  font-size: 0.82rem;
}

.oa-tech-table code {
  background: rgba(46, 52, 130, 0.07);
  color: var(--theme-default);
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
  font-size: 0.82rem;
}

.oa-tech-log-detail {
  background: rgba(46, 52, 130, 0.03);
  padding: 0.7rem 0.85rem;
}

.oa-tech-log-detail summary {
  cursor: pointer;
  font-weight: 600;
  color: var(--theme-default);
  font-size: 0.82rem;
  margin-top: 0.45rem;
}

@media (max-width: 575.98px) {
  .oa-tech-meta {
    grid-template-columns: 1fr;
  }
}
</style>
